import HeatmapData, {
    CategorizedMeta,
    DatetimeMeta,
    HeatmapAxis,
    HeatmapPalette,
    HeatmapSeries,
    HeatmapWidgetData,
    DayHourMeta,
} from 'domain/entity/widgets/HeatmapData';
import { JsonDateTimeChartAxisMeta } from 'adapter/out/client/widgets/data/mappers/ChartMapper';
import {
    JsonColor,
    JsonWidgetData,
    mapColor,
    mapDataAvailability,
    mapDate,
    mapDescription,
} from 'adapter/out/client/widgets/data/mappers/common';
import { ChartAxisType, Resolution } from 'domain/entity/widgets/ChartData';

type JsonHeatmapValue = { x: string; y: string; v: number };

type JsonHeatmapSeries = {
    values: JsonHeatmapValue[];
    palette: JsonPalette;
    title: string;
    units: string;
};

type JsonPalette = {
    colors: JsonColor[];
    positions: number[];
};

type JsonCategorizedMeta = {
    categories: string[];
};

type JsonHeatmapAxis = {
    type: string;
    title: string;
    units: string;
    meta: JsonDateTimeChartAxisMeta | JsonCategorizedMeta;
};

export type JsonHeatmapData = JsonWidgetData & {
    data: {
        series: JsonHeatmapSeries;
        ['axis_x']: JsonHeatmapAxis;
        ['axis_y']: JsonHeatmapAxis;
    };
};

const mapHeatmapMeta = (
    meta: JsonCategorizedMeta | JsonDateTimeChartAxisMeta,
    axisType: ChartAxisType
): DatetimeMeta | CategorizedMeta | DayHourMeta => {
    switch (axisType) {
        case ChartAxisType.CATEGORIZED: {
            return {
                categories: (meta as JsonCategorizedMeta).categories,
            };
        }
        case ChartAxisType.DAY_HOURS: {
            return {};
        }
        default:
        case ChartAxisType.DATETIME: {
            return {
                timezone: (meta as JsonDateTimeChartAxisMeta).timezone,
                resolution: Resolution[(meta as JsonDateTimeChartAxisMeta).resolution],
                from: mapDate((meta as JsonDateTimeChartAxisMeta).from),
                to: mapDate((meta as JsonDateTimeChartAxisMeta).to),
            };
        }
    }
};

const asHeatmapAxis = (axisY: JsonHeatmapAxis): HeatmapAxis => ({
    type: axisY.type as ChartAxisType,
    title: axisY.title,
    units: axisY.units,
    meta: mapHeatmapMeta(axisY.meta, axisY.type as ChartAxisType),
});

const mapPalette = (palette: JsonPalette): HeatmapPalette => ({
    colors: palette.colors.map((color) => mapColor(color)),
    positions: palette.positions,
});

const asSeries = (json: JsonHeatmapSeries, axisXType: string): HeatmapSeries => ({
    values: json.values.map((value: JsonHeatmapValue) => ({
        x: axisXType === ChartAxisType.DATETIME ? mapDate(value.x) : value.x,
        y: value.y,
        v: value.v,
    })),
    palette: mapPalette(json.palette),
    title: json.title,
    units: json.units,
});

export const asHeatmapData = (json: JsonHeatmapData): HeatmapWidgetData =>
    new HeatmapData(
        mapDescription(json.description),
        asSeries(json.data.series, json.data.axis_x.type),
        asHeatmapAxis(json.data.axis_x),
        asHeatmapAxis(json.data.axis_y),
        mapDataAvailability(json.data_availability)
    );
